/**
 * HR "Set leave entitlements" page — /hr/leave-import
 *
 * Four-step guided wizard for HR to set employees' leave entitlements from a
 * CSV (optionally also loading days already taken from another system):
 *   1. Prepare — plain-language guidance + template download
 *   2. Upload  — file picker, parse + dry-run
 *   3. Preview — dry-run table + summary
 *   4. Done    — success confirmation
 *
 * Step sub-components live in ./hr-leave-import/LeaveImportSteps.tsx.
 */

import { useState } from 'react'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { FadeIn } from '../components/primitives/FadeIn'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import {
  PrepareStep,
  UploadStep,
  PreviewStep,
  DoneStep,
} from './hr-leave-import/LeaveImportSteps'
import type { LeaveImportResult, LeaveImportRow } from '../api/hr-leave-import'

// ── Types ─────────────────────────────────────────────────────────────────────

type Step = 'prepare' | 'upload' | 'preview' | 'done'

const STEP_ORDER: Step[] = ['prepare', 'upload', 'preview', 'done']

const STEP_LABELS: Record<Step, string> = {
  prepare: 'Prepare your file',
  upload: 'Upload',
  preview: 'Check the preview',
  done: 'Done',
}

// ── Step indicator ────────────────────────────────────────────────────────────

interface StepIndicatorProps {
  current: Step
}

function StepIndicator({ current }: StepIndicatorProps): JSX.Element {
  const currentIdx = STEP_ORDER.indexOf(current)
  return (
    <div
      style={{
        display: 'flex',
        gap: 20,
        marginTop: -16,
        marginBottom: 24,
        padding: '12px 16px',
        background: 'var(--surface-2)',
        borderRadius: 8,
        flexWrap: 'wrap',
      }}
    >
      {STEP_ORDER.map((key, i) => {
        const done = i < currentIdx
        const active = key === current
        return (
          <div
            key={key}
            aria-current={active ? 'step' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                fontSize: 12,
                fontWeight: 700,
                background: done
                  ? 'var(--success-fg)'
                  : active
                    ? 'var(--steel-blue)'
                    : 'var(--surface-2)',
                color: done || active ? 'var(--surface)' : 'var(--text-3)',
                border: active || done ? 'none' : '1px solid var(--border)',
                flexShrink: 0,
              }}
            >
              {done ? '✓' : i + 1}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                color: active ? 'var(--text)' : 'var(--text-3)',
              }}
            >
              {STEP_LABELS[key]}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ── Page inner ────────────────────────────────────────────────────────────────

function HrLeaveBalanceImportInner(): JSX.Element {
  const [step, setStep] = useState<Step>('prepare')
  const [parsedRows, setParsedRows] = useState<LeaveImportRow[]>([])
  const [previewResult, setPreviewResult] = useState<LeaveImportResult | null>(null)

  function handlePreviewReady(rows: LeaveImportRow[], result: LeaveImportResult): void {
    setParsedRows(rows)
    setPreviewResult(result)
    setStep('preview')
  }

  function handleReset(): void {
    setParsedRows([])
    setPreviewResult(null)
    setStep('prepare')
  }

  return (
    <>
      <PageHead eyebrow="HR" title="Set leave entitlements" />

      <StepIndicator current={step} />

      <Card padding={24}>
        <FadeIn triggerKey={step} rise={4} duration="fast">
          {step === 'prepare' ? (
            <PrepareStep onNext={() => setStep('upload')} />
          ) : step === 'upload' ? (
            <UploadStep
              onPreviewReady={handlePreviewReady}
              onBack={() => setStep('prepare')}
            />
          ) : step === 'preview' && previewResult != null ? (
            <PreviewStep
              rows={parsedRows}
              preview={previewResult}
              onBack={() => setStep('upload')}
              onCommit={(result) => {
                // Show the committed result (post-skip counts), not the dry-run.
                setPreviewResult(result)
                setStep('done')
              }}
            />
          ) : step === 'done' && previewResult != null ? (
            <DoneStep preview={previewResult} onReset={handleReset} />
          ) : null}
        </FadeIn>
      </Card>
    </>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function HrLeaveBalanceImportPage(): JSX.Element {
  return (
    <HrAccessGuard>
      <HrLeaveBalanceImportInner />
    </HrAccessGuard>
  )
}
